import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';

interface TourAudioPlayerProps {
  tour: {
    id: string;
    city: string;
    title: string;
    latitude?: number;
    longitude?: number;
  };
}

const TourAudioPlayer: React.FC<TourAudioPlayerProps> = ({ tour }) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const playbackRates = [1, 1.25, 1.5, 2];

  // Fetch audio URL based on tour coordinates
  useEffect(() => {
    const fetchAudioUrl = async () => {
      if (!tour.latitude || !tour.longitude) {
        setLoading(false);
        return;
      }

      try {
        // Find podcast with matching coordinates (within a small radius)
        const { data: podcasts, error } = await supabase
          .from('podcasts')
          .select('AudioFile_EN, Name, Latitude, Longitude')
          .not('AudioFile_EN', 'is', null);

        if (error) {
          console.error('Error fetching podcasts:', error);
          setLoading(false);
          return;
        }

        // Find podcast with exactly matching coordinates
        const matchingPodcast = podcasts?.find(podcast => 
          podcast.Latitude && podcast.Longitude &&
          podcast.Latitude === tour.latitude &&
          podcast.Longitude === tour.longitude
        );

        if (matchingPodcast?.AudioFile_EN) {
          setAudioUrl(matchingPodcast.AudioFile_EN);
        }
      } catch (error) {
        console.error('Error fetching audio:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchAudioUrl();
  }, [tour.latitude, tour.longitude]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleDurationChange = () => setDuration(audio.duration);
    const handleLoadedData = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleDurationChange);
    audio.addEventListener('loadeddata', handleLoadedData);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleDurationChange);
      audio.removeEventListener('loadeddata', handleLoadedData);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [audioUrl]);

  const togglePlayPause = () => {
    const audio = audioRef.current;
    if (!audio) return;

    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const handleSeek = (e: React.ChangeEvent<HTMLInputElement>) => {
    const audio = audioRef.current;
    if (!audio) return;

    const newTime = parseFloat(e.target.value);
    audio.currentTime = newTime;
    setCurrentTime(newTime);
  };

  const cyclePlaybackRate = () => {
    const audio = audioRef.current;
    if (!audio) return;

    const currentIndex = playbackRates.indexOf(playbackRate);
    const nextIndex = (currentIndex + 1) % playbackRates.length;
    const newRate = playbackRates[nextIndex];
    
    audio.playbackRate = newRate;
    setPlaybackRate(newRate);
  };

  const formatTime = (time: number) => {
    if (isNaN(time)) return '0:00';
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const progressPercentage = duration > 0 ? (currentTime / duration) * 100 : 0;

  if (loading) {
    return (
      <div className="bg-card/50 backdrop-blur-sm border border-border/40 rounded-xl p-4 mb-6">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-full bg-muted animate-pulse" />
          <div className="flex-1">
            <div className="h-2 bg-muted rounded-full animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (!audioUrl) {
    return null; // Don't show player if no audio available
  }

  return (
    <div className="bg-card/50 backdrop-blur-sm border border-border/40 rounded-xl p-4 mb-6">
      <audio
        ref={audioRef}
        src={audioUrl}
        preload="metadata"
      />
      
      <div className="flex items-center gap-4">
        {/* Play/Pause Button */}
        <Button
          variant="ghost"
          size="sm"
          onClick={togglePlayPause}
          className="w-10 h-10 rounded-full bg-primary/10 hover:bg-primary/20 text-primary"
        >
          {isPlaying ? (
            <Pause className="w-5 h-5" />
          ) : (
            <Play className="w-5 h-5 ml-0.5" />
          )}
        </Button>

        {/* Progress Section */}
        <div className="flex-1 flex items-center gap-3">
          <span className="text-xs text-muted-foreground font-mono min-w-[2.5rem]">
            {formatTime(currentTime)}
          </span>
          
          {/* Progress Bar */}
          <div className="flex-1 relative">
            <div className="h-2 bg-muted rounded-full overflow-hidden">
              <div 
                className="h-full bg-primary transition-all duration-300 ease-out"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
            <input
              type="range"
              min="0"
              max={duration || 0}
              value={currentTime}
              onChange={handleSeek}
              className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            />
          </div>
          
          <span className="text-xs text-muted-foreground font-mono min-w-[2.5rem]">
            {formatTime(duration)}
          </span>
        </div>

        {/* Speed Control */}
        <Button
          variant="ghost"
          size="sm"
          onClick={cyclePlaybackRate}
          className="px-3 h-8 text-xs font-medium text-muted-foreground hover:text-foreground"
        >
          {playbackRate}x
        </Button>
      </div>
    </div>
  );
};

export default TourAudioPlayer;